import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Device } from "@/types/device";
import { useState } from "react";
import * as devicesApi from "@/services/devicesApi";

export type { Device, InstalledApp } from "@/types/device";

export const DEVICES_QUERY_KEY = ['devices'];

export const useDevices = (params: { page?: number; pageSize?: number } = {}) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(params.page || 1);
  const [pageSize] = useState(params.pageSize || 25);

  const query = useQuery({
    queryKey: [...DEVICES_QUERY_KEY, page, pageSize],
    queryFn: async () => {
      const res = await devicesApi.listDevices(page - 1, pageSize);
      const devices: Device[] = (res.data || []).map((d: any) => ({
        id: d.uuid || d.id,
        name: d.deviceName || d.name || 'Unknown Device',
        os_type: d.osType || d.os_type || '',
        os_version: d.osVersion || d.os_version || '',
        status: d.status || 'unknown',
        last_seen: d.lastSeen || d.last_seen || d.createdAt || '',
        created_at: d.createdAt || d.created_at || '',
        hostname: d.hostname || '',
        battery_level: d.batteryLevel ?? null,
        battery_status: d.batteryStatus ?? null,
        ip_address: d.ipAddress ?? null,
        serial_number: d.serialNumber || null,
        installed_apps: [],
        department: null,
        location: null,
        customer: d.tenantName || null,
        tenantId: d.tenantId || null,
      }));
      const pagination = res.meta?.pagination;
      const total = pagination?.totalElements || devices.length;
      const totalPages = pagination?.totalPages || 1;
      return { devices, total, totalPages };
    },
    staleTime: 1000 * 30,
    refetchOnMount: true,
  });

  return {
    ...query,
    data: query.data?.devices || [],
    total: query.data?.total || 0,
    page,
    pageSize,
    totalPages: query.data?.totalPages || 0,
    deviceTags: new Map(),
    statusCounts: { active: 0, inactive: 0, pending: 0, total: 0 },
    setPage,
  };
};

export const getTenantInfo = (_deviceId: string) => ({
  customer: '',
  tenantId: '',
});

export const invalidateDevicesCache = () => {
  if (typeof window !== 'undefined') {
    const event = new Event('device-cache-updated');
    window.dispatchEvent(event);
  }
};
